import { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';
import { Guid } from '../../../api/baseApi';
import { getTVChannels, TVChannel } from '../../../api/tvChannelApi';
import { sortByNameString } from '../../../services/common';
import { Button } from '../Button';
import { FormInput, FormRow } from '../formRow/FormRow';
import GlobalSpinner from '../globalsSpinner/GlobalSpinner'
import IconButton from '../iconButton/IconButton';
import { RightModal, SmallModal } from '../sideModal/Modal';
import { TvChannelOverrideCreateRequest, TvChannelOverrideUpdateRequest, TvChannelOverrided, createTVChannelOverrride, deleteTVChannelOverride, getTVChannelOverrides, updateTVChannelOverride } from '../../../api/playersApi';

type ModalAction = "create" | "edit" | "nothing";

const TvChannelMappingOverrides:FC = () => {
    let [isLoading, setIsLoading] = useState(false);

    let [tvChannelsList, setTvChannelsList] = useState<TVChannel[]>([]);
    let [selectedTvChannel, setSelectedTvChannel] = useState<SingleValue<TVChannel>>(null);
    let [isSelectedTvChannelError, setIsSelectedTvChannelError] = useState(false);

    let [tvChannelOverridesList, setTvChannelOverridesList] = useState<TvChannelOverrided[]>([]);
    let [tvChannelOverrideIdForAction, setTvChannelOverrideIdForAction] = useState<Guid>('');
    let [modalAction, setModalAction] = useState<ModalAction>("nothing");

    let [overrideValue, setOverriedValue] = useState("");
    let [isOverrideValueError, setIsOverrideValueError] = useState(false);

    let [isShowDeleteTvChannelOverrideModal, setIsShowDeleteTvChannelOverrideModal] = useState(false);

    let {id : playerId} = useParams<{id:string}>();

    useEffect(()=>{
        async function fetchData() {
            setIsLoading(true);
            await Promise.all([updateTvChannelOverridedList(),updateTvChannelList()]);
            setIsLoading(false);
        }

        fetchData()
    },[]);

    const updateTvChannelOverridedList = async () => {
        let response = await getTVChannelOverrides(playerId);
        if( response.type === "succes") {
            setTvChannelOverridesList(response.data.items);
        }
    }

    const updateTvChannelList = async () => {
        let response = await getTVChannels();
        if( response.type === "succes") {
            setTvChannelsList(response.data.items);
        }
    }

    const overrideHandler = (channelNumber: string) => {
        setIsOverrideValueError(false);
        setOverriedValue(channelNumber)
    }

    const showTvChannelOverrideModal = (action: ModalAction, tvChannelOverride?: TvChannelOverrided) => {
        setModalAction(action);
        if(tvChannelOverride){
            setTvChannelOverrideIdForAction(tvChannelOverride.id)
            let t = tvChannelsList.find(it => it.id === tvChannelOverride.tvChannelId);
            setSelectedTvChannel(t ? t : null);
            setOverriedValue(tvChannelOverride.playerValue);
        }
    }

    const hideTvChannelOverrideModal = () => {
        setModalAction("nothing");
        setIsOverrideValueError(false);
        setOverriedValue('');
        setIsSelectedTvChannelError(false);
        setSelectedTvChannel(null);
    }

    const createOrUpdateTvChannelMappingOverride = async () => {
        let isValid = true;
        if(!selectedTvChannel){
            setIsSelectedTvChannelError(true);
            isValid = false;
        }

        if(!overrideValue){
            setIsOverrideValueError(true);
            isValid = false;
        }    
        
        if(!isValid) return;

        setIsLoading(true)
        if( modalAction === "edit" && selectedTvChannel ) {
            let request:TvChannelOverrideUpdateRequest = {
                command: overrideValue
            }
            await updateTVChannelOverride(playerId, tvChannelOverrideIdForAction, request)
        }

        if( modalAction === "create" && selectedTvChannel ) {
            let request:TvChannelOverrideCreateRequest = {
                tvChannelId: selectedTvChannel!.id,
                command: overrideValue
            }
            await createTVChannelOverrride( playerId, request)
        }

        hideTvChannelOverrideModal();
        await updateTvChannelOverridedList();
        setIsLoading(false);
    }

    const showDeleteTvChannelOverrideModal = (id:Guid) => {
        setIsShowDeleteTvChannelOverrideModal(true);
        setTvChannelOverrideIdForAction(id);
    }

    const hideDeleteTvChannelOverrrideModal = () => {
        setIsShowDeleteTvChannelOverrideModal(false);
    }

    const removeTvChannelOverride = async () => {
        setIsLoading(true);
        await deleteTVChannelOverride(playerId, tvChannelOverrideIdForAction);
        await updateTvChannelOverridedList();
        setIsShowDeleteTvChannelOverrideModal(false);
        setIsLoading(false);
    }

    const onSelectedTvChannel = (newValue: SingleValue<TVChannel>) => {
        setIsSelectedTvChannelError(false);
        setSelectedTvChannel(newValue);
    }

    const renderTvChannelOverrideList = ( items: TvChannelOverrided[]) => {
        return items.map( it => {
            return <tr key={it.tvChannelId}>
                <td>{it.tvChannelName}</td>
                <td>{it.originalValue}</td>
                <td>{it.playerValue}</td>
                <td>
                    <IconButton tooltipText="Edit" type="edit" onClick={() => showTvChannelOverrideModal("edit", it)} />
                    <IconButton tooltipText="Delete" type="delete" onClick={() => showDeleteTvChannelOverrideModal(it.id)} />
                </td>
            </tr>
        })
    }

    return <>
        <div className="d-flex justify-content-between align-items-center mb-20 w-100">
            <h2>TV Channel Mapping Overrides</h2>
            <Button 
                color="green"  
                label="Add TV Channel Mapping Override" 
                onClick={() => showTvChannelOverrideModal("create")} 
            />
        </div>

        {tvChannelOverridesList.length ? <table className="table">
            <thead>
                <tr>
                    <td>TV Channel Name</td>
                    <td>Original Value</td>
                    <td>Player Value</td>
                    <td></td>
                </tr>
            </thead>
            <tbody>
                {renderTvChannelOverrideList(tvChannelOverridesList)}
            </tbody>
        </table>: <p>Element list is empty</p>}

        <RightModal title={modalAction === "edit" ? "Edit TV Channel Mapping Override" : "Add TV Channel Mapping Override"} show={modalAction !== "nothing"} onRequestClose={hideTvChannelOverrideModal}>
            <FormRow>
                <label>TV Channel</label>
                <Select 
                    className={`${isSelectedTvChannelError ? "error" : ""}`}
                    classNamePrefix={`select`}
                    options={tvChannelsList.filter(it => !tvChannelOverridesList.some(it2 => it2.tvChannelId === it.id))} 
                    filterOption={(option, input)=> option.label.toLowerCase().includes(input.toLowerCase())}
                    getOptionLabel={(option) => `${option.name}`}
                    getOptionValue={(option) => `${option.id}`}
                    onChange={onSelectedTvChannel}
                    value={selectedTvChannel}
                    isDisabled={modalAction === "edit"}
                />    
            </FormRow>

            <FormRow>
                <FormInput
                    errorMessage = "Please enter Override Value"
                    id = "override_value_input"
                    isError = {isOverrideValueError}
                    title = "Override Value"
                    onChangeValue = {(e)=>{overrideHandler(e)}}
                    value = {overrideValue}
                />
            </FormRow>

            <div className="modal-btn-group">
                <Button color="green" label="Save" onClick={createOrUpdateTvChannelMappingOverride}/>
                <Button color="gray" label="Cancel" onClick={hideTvChannelOverrideModal}/>
            </div>
        </RightModal>  

        <SmallModal show={isShowDeleteTvChannelOverrideModal} onRequestClose={hideDeleteTvChannelOverrrideModal}> 
            <p>That you want to delete TV Channel Mapping Override?</p>
            <div className="modal-btn-group">
                <Button color="gray" label="Cancel" onClick={hideDeleteTvChannelOverrrideModal}/>
                <Button color="green" label="Yes" onClick={() => removeTvChannelOverride()}/>
            </div>
        </SmallModal>
        
        <GlobalSpinner isShow={isLoading} />
    </>
}

export default TvChannelMappingOverrides
